import { FC, useState, useEffect, useMemo } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Drawer } from '../ui/Drawer';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Badge } from '../ui/Badge';
import { DispatchQueue, UpdateDispatchQueuePayload } from '../../api/queue';

export interface PacingProfileState {
  pacing_mode: 'dynamic' | 'aggressive' | 'conservative' | 'custom' | string;
  quiet_hours_enabled: boolean;
  quiet_hours_start: string;
  quiet_hours_end: string;
  prime_start_hour: number;
  prime_end_hour: number;
  prime_min_delay_minutes: number;
  prime_max_delay_minutes: number;
  day_min_delay_minutes: number;
  day_max_delay_minutes: number;
}

const PRESET_PROFILES: Record<string, PacingProfileState> = {
  dynamic: {
    pacing_mode: 'dynamic',
    quiet_hours_enabled: true,
    quiet_hours_start: '22:30',
    quiet_hours_end: '07:30',
    prime_start_hour: 18,
    prime_end_hour: 22,
    prime_min_delay_minutes: 3,
    prime_max_delay_minutes: 5,
    day_min_delay_minutes: 8,
    day_max_delay_minutes: 15,
  },
  aggressive: {
    pacing_mode: 'aggressive',
    quiet_hours_enabled: true,
    quiet_hours_start: '23:00',
    quiet_hours_end: '07:00',
    prime_start_hour: 17,
    prime_end_hour: 23,
    prime_min_delay_minutes: 2,
    prime_max_delay_minutes: 4,
    day_min_delay_minutes: 5,
    day_max_delay_minutes: 8,
  },
  conservative: {
    pacing_mode: 'conservative',
    quiet_hours_enabled: true,
    quiet_hours_start: '22:00',
    quiet_hours_end: '08:00',
    prime_start_hour: 18,
    prime_end_hour: 22,
    prime_min_delay_minutes: 5,
    prime_max_delay_minutes: 10,
    day_min_delay_minutes: 15,
    day_max_delay_minutes: 30,
  },
};

const PRESET_OPTIONS = [
  {
    id: 'dynamic',
    name: 'Dinâmico',
    primeHours: '18h-22h',
    primeDelay: '3-5 min',
    commercialDelay: '8-15 min',
    quietHours: '22:30-07:30',
  },
  {
    id: 'aggressive',
    name: 'Agressivo',
    primeHours: '17h-23h',
    primeDelay: '2-4 min',
    commercialDelay: '5-8 min',
    quietHours: '23:00-07:00',
  },
  {
    id: 'conservative',
    name: 'Conservador',
    primeHours: '18h-22h',
    primeDelay: '5-10 min',
    commercialDelay: '15-30 min',
    quietHours: '22:00-08:00',
  },
];

function parseTimeToDecimal(timeStr: string, fallback: number): number {
  if (!timeStr || !timeStr.includes(':')) return fallback;
  const parts = timeStr.split(':').map(Number);
  if (isNaN(parts[0])) return fallback;
  const m = isNaN(parts[1]) ? 0 : parts[1];
  return Math.min(24, Math.max(0, parts[0] + m / 60));
}

function getSmoothPath(points: { x: number; y: number }[]): string {
  if (points.length === 0) return '';
  let d = `M ${points[0].x.toFixed(1)},${points[0].y.toFixed(1)}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i === 0 ? 0 : i - 1];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2] || p2;

    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;

    d += ` C ${cp1x.toFixed(1)},${cp1y.toFixed(1)} ${cp2x.toFixed(1)},${cp2y.toFixed(1)} ${p2.x.toFixed(1)},${p2.y.toFixed(1)}`;
  }
  return d;
}

function calculateCadenceCurve(form: PacingProfileState) {
  const primeStart = Math.min(
    24,
    Math.max(0, Number(form.prime_start_hour) || 18)
  );
  const primeEnd = Math.min(24, Math.max(0, Number(form.prime_end_hour) || 22));
  const quietEnabled = Boolean(form.quiet_hours_enabled);
  const qStart = parseTimeToDecimal(form.quiet_hours_start, 22.5);
  const qEnd = parseTimeToDecimal(form.quiet_hours_end, 7.5);

  const svgWidth = 480;
  const svgHeight = 60;
  const baselineY = 52;
  const commercialY = 32;
  const primeY = 12;

  let primeHoursTotal = 0;
  let commercialHoursTotal = 0;

  const rawPoints: {
    hour: number;
    targetY: number;
    color: string;
    type: 'quiet' | 'commercial' | 'prime';
  }[] = [];

  const sampleCount = 48; // a cada 30 minutos de 0 a 24h
  for (let i = 0; i <= sampleCount; i++) {
    const hour = (i / sampleCount) * 24;

    let isQuiet = false;
    if (quietEnabled) {
      if (qStart > qEnd) {
        isQuiet = hour >= qStart || hour < qEnd;
      } else {
        isQuiet = hour >= qStart && hour < qEnd;
      }
    }

    let isPrime = false;
    if (!isQuiet) {
      if (primeStart < primeEnd) {
        isPrime = hour >= primeStart && hour < primeEnd;
      } else {
        isPrime = hour >= primeStart || hour < primeEnd;
      }
    }

    let type: 'quiet' | 'commercial' | 'prime' = 'commercial';
    let targetY = commercialY;
    let color = '#0284c7'; // Azul/Sky para comercial

    if (isQuiet) {
      type = 'quiet';
      targetY = baselineY;
      color = '#6366f1'; // Indigo para silêncio noturno
    } else if (isPrime) {
      type = 'prime';
      targetY = primeY;
      color = '#10b981'; // Emerald para horário nobre
      primeHoursTotal += 0.5;
    } else {
      commercialHoursTotal += 0.5;
    }

    rawPoints.push({ hour, targetY, color, type });
  }

  // Suavização leve dos pontos Y para uma curva visual fluida e orgânica
  const smoothedPoints = rawPoints.map((pt, i, arr) => {
    const x = (i / sampleCount) * svgWidth;
    const prev = arr[Math.max(0, i - 1)].targetY;
    const curr = pt.targetY;
    const next = arr[Math.min(arr.length - 1, i + 1)].targetY;
    const smoothedY = prev * 0.2 + curr * 0.6 + next * 0.2;
    return { x, y: smoothedY, color: pt.color, type: pt.type };
  });

  const pathD = getSmoothPath(smoothedPoints);

  // Paradas de cor sólidas (sem degradê / sem transição suave de cor)
  // Cada fase de horário tem sua cor pura e nítida
  const strokeStops: { offset: string; color: string }[] = [];
  for (let i = 0; i < smoothedPoints.length; i++) {
    const pt = smoothedPoints[i];
    const offset = `${((i / sampleCount) * 100).toFixed(1)}%`;
    if (i === 0) {
      strokeStops.push({ offset: '0%', color: pt.color });
    } else {
      const prevColor = smoothedPoints[i - 1].color;
      if (pt.color !== prevColor) {
        // Transição instantânea sem degradê: adiciona o stop anterior e o novo stop no mesmo offset
        strokeStops.push({ offset, color: prevColor });
        strokeStops.push({ offset, color: pt.color });
      }
    }
  }
  strokeStops.push({
    offset: '100%',
    color: smoothedPoints[smoothedPoints.length - 1].color,
  });

  // Estima capacidade teórica diária por grupo
  const primeMinDelay = Math.max(1, Number(form.prime_min_delay_minutes) || 3);
  const primeMaxDelay = Math.max(1, Number(form.prime_max_delay_minutes) || 5);
  const dayMinDelay = Math.max(1, Number(form.day_min_delay_minutes) || 8);
  const dayMaxDelay = Math.max(1, Number(form.day_max_delay_minutes) || 15);

  const primeMinMsg = Math.round((primeHoursTotal * 60) / primeMaxDelay);
  const primeMaxMsg = Math.round((primeHoursTotal * 60) / primeMinDelay);
  const commMinMsg = Math.round((commercialHoursTotal * 60) / dayMaxDelay);
  const commMaxMsg = Math.round((commercialHoursTotal * 60) / dayMinDelay);

  const totalMinDaily = primeMinMsg + commMinMsg;
  const totalMaxDaily = primeMaxMsg + commMaxMsg;

  return {
    pathD,
    strokeStops,
    svgWidth,
    svgHeight,
    baselineY,
    totalMinDaily,
    totalMaxDaily,
    primeHoursTotal,
    commercialHoursTotal,
  };
}

interface PacingDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  queues: DispatchQueue[];
  selectedQueueId: string;
  onSave: (
    targetQueueId: string,
    payload: UpdateDispatchQueuePayload
  ) => Promise<void>;
}

export const PacingDrawer: FC<PacingDrawerProps> = ({
  isOpen,
  onClose,
  queues,
  selectedQueueId,
  onSave,
}) => {
  const [targetQueueId, setTargetQueueId] = useState<string>(selectedQueueId);
  const [isSaving, setIsSaving] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);
  const [form, setForm] = useState<PacingProfileState>({
    ...PRESET_PROFILES.dynamic,
  });

  const populateFormFromQueue = (q?: DispatchQueue) => {
    if (!q) {
      setForm({ ...PRESET_PROFILES.dynamic });
      return;
    }
    setForm({
      pacing_mode: q.pacing_mode || 'dynamic',
      quiet_hours_enabled: q.quiet_hours_enabled ?? true,
      quiet_hours_start: q.quiet_hours_start || '22:30',
      quiet_hours_end: q.quiet_hours_end || '07:30',
      prime_start_hour: q.prime_start_hour ?? 18,
      prime_end_hour: q.prime_end_hour ?? 22,
      prime_min_delay_minutes: q.prime_min_delay_minutes ?? 3,
      prime_max_delay_minutes: q.prime_max_delay_minutes ?? 5,
      day_min_delay_minutes: q.day_min_delay_minutes ?? 8,
      day_max_delay_minutes: q.day_max_delay_minutes ?? 15,
    });
  };

  useEffect(() => {
    if (!isOpen) return;

    const validTarget =
      targetQueueId === 'all' || queues.some((q) => q.id === targetQueueId)
        ? targetQueueId
        : selectedQueueId === 'all'
          ? queues[0]?.id || 'all'
          : selectedQueueId;

    setTargetQueueId(validTarget);
    const activeQ =
      validTarget === 'all'
        ? queues[0]
        : queues.find((q) => q.id === validTarget);
    populateFormFromQueue(activeQ);
  }, [isOpen, selectedQueueId, queues]);

  const handleApplyPreset = (mode: string) => {
    const preset = PRESET_PROFILES[mode];
    if (preset) {
      setForm({ ...preset });
    }
  };

  const handleQueueChange = (newQueueId: string) => {
    setTargetQueueId(newQueueId);
    const q =
      newQueueId === 'all'
        ? queues[0]
        : queues.find((item) => item.id === newQueueId);
    populateFormFromQueue(q);
  };

  const handleSubmit = async () => {
    setIsSaving(true);
    try {
      await onSave(targetQueueId, {
        pacing_mode: form.pacing_mode,
        quiet_hours_enabled: form.quiet_hours_enabled,
        quiet_hours_start: form.quiet_hours_start,
        quiet_hours_end: form.quiet_hours_end,
        prime_start_hour: Number(form.prime_start_hour),
        prime_end_hour: Number(form.prime_end_hour),
        prime_min_delay_minutes: Number(form.prime_min_delay_minutes),
        prime_max_delay_minutes: Number(form.prime_max_delay_minutes),
        day_min_delay_minutes: Number(form.day_min_delay_minutes),
        day_max_delay_minutes: Number(form.day_max_delay_minutes),
      });
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  const cadenceChart = useMemo(() => calculateCadenceCurve(form), [form]);

  // Validação de coerência operacional
  const hasTimeConflict =
    Number(form.prime_start_hour) >= Number(form.prime_end_hour);
  const hasDelayConflict =
    Number(form.prime_min_delay_minutes) >
      Number(form.prime_max_delay_minutes) ||
    Number(form.day_min_delay_minutes) > Number(form.day_max_delay_minutes);

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title="Configurar Ritmo de Disparo"
      width="2xl"
      bodyClassName="p-0"
      footer={
        <div className="flex items-center justify-between gap-3">
          <div className="text-micro text-content-muted">
            Estimativa:{' '}
            <strong className="text-content-main">
              ~{cadenceChart.totalMinDaily} a {cadenceChart.totalMaxDaily}{' '}
              envios/dia
            </strong>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              size="sm"
              isLoading={isSaving}
              onClick={handleSubmit}
              disabled={hasTimeConflict || hasDelayConflict}
            >
              Salvar Alterações
            </Button>
          </div>
        </div>
      }
    >
      <div className="flex flex-col">
        {/* BLOCO FIXO NO TOPO: Fundo 100% Opaco, sem gaps, colado no topo do scroll */}
        <div className="sticky top-0 z-20 w-full border-b border-surface-border bg-surface-panel px-5 py-4">
          {/* Linha 1: Seletor de Fila e Alternador de Ajuda */}
          <div className="flex items-center justify-between gap-3">
            <div className="flex flex-1 items-center gap-2">
              <label
                htmlFor="pacing-queue-select"
                className="whitespace-nowrap text-xs font-semibold text-content-main"
              >
                Fila:
              </label>
              <select
                id="pacing-queue-select"
                value={targetQueueId}
                onChange={(e) => handleQueueChange(e.target.value)}
                className="focus:outline-hidden w-full max-w-xs rounded-lg border border-surface-border bg-surface-subtle px-2.5 py-1.5 text-xs font-medium text-content-main focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
              >
                <option value="all">Todas as Filas (Aplicar em lote)</option>
                {queues.map((q) => (
                  <option key={q.id} value={q.id}>
                    {q.name} ({q.slug})
                  </option>
                ))}
              </select>
            </div>

            <button
              type="button"
              onClick={() => setShowExplanation(!showExplanation)}
              className="inline-flex cursor-pointer items-center gap-1 text-micro font-medium text-content-muted transition-colors hover:text-content-main"
            >
              <span>{showExplanation ? 'Ocultar Guia' : 'Como Funciona?'}</span>
              {showExplanation ? (
                <ChevronUp className="h-3 w-3" />
              ) : (
                <ChevronDown className="h-3 w-3" />
              )}
            </button>
          </div>

          {/* Explicação técnica sob demanda */}
          {showExplanation && (
            <div className="mt-3 rounded-lg border border-brand-500/20 bg-brand-500/5 p-3 text-micro leading-relaxed text-content-main">
              O motor de ritmo distribui as mensagens ao longo do dia para
              evitar picos robóticos. Ele acelera no Horário Nobre, espaça os
              disparos em horário comercial com intervalo aleatório (Jitter) e
              suspende envios na madrugada reagendando os itens automaticamente.
            </div>
          )}

          {/* Linha 2: Gráfico Fluido de Fluxo de Mensagens (24h) */}
          <div className="mt-3.5 space-y-1.5">
            <div className="flex items-center justify-between text-2xs font-semibold text-content-muted">
              <span>Fluxo de Mensagens ao Longo do Dia (24 Horas)</span>
              <span>
                {form.quiet_hours_enabled
                  ? 'Silêncio Ativo'
                  : 'Silêncio Desativado'}
              </span>
            </div>

            {/* Curva Fluida e Colorida por Fase SVG */}
            <div className="relative overflow-hidden rounded-xl border border-surface-border bg-surface-canvas p-2 shadow-inner">
              <svg
                viewBox={`0 0 ${cadenceChart.svgWidth} ${cadenceChart.svgHeight}`}
                className="h-14 w-full"
                preserveAspectRatio="none"
              >
                <defs>
                  <linearGradient
                    id="cadence-stroke-gradient"
                    x1="0"
                    y1="0"
                    x2="1"
                    y2="0"
                  >
                    {cadenceChart.strokeStops.map((stop, idx) => (
                      <stop
                        key={idx}
                        offset={stop.offset}
                        stopColor={stop.color}
                      />
                    ))}
                  </linearGradient>
                </defs>

                {/* Linhas de grade horária (06h, 12h, 18h) */}
                <line
                  x1="120"
                  y1="6"
                  x2="120"
                  y2="54"
                  stroke="currentColor"
                  strokeOpacity="0.1"
                  strokeDasharray="2,2"
                />
                <line
                  x1="240"
                  y1="6"
                  x2="240"
                  y2="54"
                  stroke="currentColor"
                  strokeOpacity="0.1"
                  strokeDasharray="2,2"
                />
                <line
                  x1="360"
                  y1="6"
                  x2="360"
                  y2="54"
                  stroke="currentColor"
                  strokeOpacity="0.1"
                  strokeDasharray="2,2"
                />

                {/* Linha base */}
                <line
                  x1="0"
                  y1={cadenceChart.baselineY}
                  x2={cadenceChart.svgWidth}
                  y2={cadenceChart.baselineY}
                  stroke="currentColor"
                  strokeOpacity="0.2"
                />

                {/* Linha de contorno suave e multicolorida pura (sem degradê) */}
                <path
                  d={cadenceChart.pathD}
                  fill="none"
                  stroke="url(#cadence-stroke-gradient)"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>

              {/* Marcações de Horário no Eixo X */}
              <div className="text-content-muted/80 flex justify-between px-0.5 pt-1 font-mono text-2xs">
                <span>00:00</span>
                <span>06:00</span>
                <span>12:00</span>
                <span>18:00</span>
                <span>24:00</span>
              </div>
            </div>

            {/* Pílulas de Legenda Direta */}
            <div className="flex flex-wrap items-center justify-between gap-1.5 pt-1 text-2xs">
              <span className="inline-flex items-center gap-1.5 text-indigo-500 dark:text-indigo-400">
                <span className="h-2 w-2 rounded-full bg-indigo-500" />
                <span>
                  Silêncio:{' '}
                  {form.quiet_hours_enabled
                    ? `${form.quiet_hours_start} - ${form.quiet_hours_end}`
                    : 'Desativado'}
                </span>
              </span>

              <span className="inline-flex items-center gap-1.5 text-sky-600 dark:text-sky-400">
                <span className="h-2 w-2 rounded-full bg-sky-500" />
                <span>
                  Comercial: {form.day_min_delay_minutes} a{' '}
                  {form.day_max_delay_minutes} min
                </span>
              </span>

              <span className="inline-flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400">
                <span className="h-2 w-2 rounded-full bg-emerald-500" />
                <span>
                  Nobre: {form.prime_start_hour}h às {form.prime_end_hour}h (
                  {form.prime_min_delay_minutes} a{' '}
                  {form.prime_max_delay_minutes} min)
                </span>
              </span>
            </div>
          </div>
        </div>

        {/* ÁREA ROLÁVEL COM OS PERFIS E CAMPOS */}
        <div className="space-y-6 p-5">
          {/* Seção 1: Perfis Pré-configurados (Compactos em Grid) */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-bold text-content-main">
                Perfis Pré-configurados
              </h4>
              {form.pacing_mode === 'custom' && (
                <Badge variant="warning">Personalizado</Badge>
              )}
            </div>

            <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-3">
              {PRESET_OPTIONS.map((opt) => {
                const isSelected = form.pacing_mode === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => handleApplyPreset(opt.id)}
                    className={`cursor-pointer rounded-xl border p-2.5 text-left transition-all ${
                      isSelected
                        ? 'border-brand-500 bg-brand-500/10 ring-1 ring-brand-500'
                        : 'border-surface-border bg-surface-panel hover:bg-surface-subtle'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-content-main">
                        {opt.name}
                      </span>
                      <div
                        className={`flex h-3.5 w-3.5 items-center justify-center rounded-full border transition-colors ${
                          isSelected
                            ? 'border-brand-500 bg-brand-500'
                            : 'border-surface-border bg-surface-canvas'
                        }`}
                      >
                        {isSelected && (
                          <span className="h-1 w-1 rounded-full bg-white" />
                        )}
                      </div>
                    </div>

                    <div className="border-surface-border/50 mt-2 space-y-1 border-t pt-1.5 font-mono text-2xs">
                      <div className="flex items-center justify-between gap-1">
                        <span className="shrink-0 text-content-muted">
                          Nobre:
                        </span>
                        <span className="whitespace-nowrap font-semibold text-emerald-600 dark:text-emerald-400">
                          {opt.primeHours} ({opt.primeDelay})
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-1">
                        <span className="shrink-0 text-content-muted">
                          Comercial:
                        </span>
                        <span className="whitespace-nowrap font-medium text-sky-600 dark:text-sky-400">
                          {opt.commercialDelay}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-1">
                        <span className="shrink-0 text-content-muted">
                          Silêncio:
                        </span>
                        <span className="whitespace-nowrap font-medium text-indigo-500 dark:text-indigo-400">
                          {opt.quietHours}
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Seção 2: Configurações Manuais em 2 Blocos Compactos */}
          <div className="space-y-4">
            <h4 className="text-xs font-bold text-content-main">
              Ajuste Fino de Parâmetros
            </h4>

            {/* Bloco A: Janelas de Horário (Nobre & Silêncio) */}
            <div className="space-y-4 rounded-xl border border-surface-border bg-surface-panel p-4">
              <div className="border-surface-border/60 flex items-center justify-between border-b pb-2">
                <span className="text-xs font-semibold text-content-main">
                  Janelas de Horário
                </span>
                <label className="flex cursor-pointer items-center gap-2">
                  <input
                    type="checkbox"
                    checked={form.quiet_hours_enabled}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        pacing_mode: 'custom',
                        quiet_hours_enabled: e.target.checked,
                      })
                    }
                    className="h-4 w-4 rounded-sm border-surface-border text-brand-500 focus:ring-brand-500"
                  />
                  <span className="text-micro font-medium text-content-main">
                    Silêncio Noturno Ativado
                  </span>
                </label>
              </div>

              {/* Linha 1: Horário Nobre */}
              <div>
                <span className="mb-2 block text-2xs font-medium text-content-muted">
                  Horário Nobre (Horas de 0 a 24)
                </span>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Input
                    label="Início"
                    type="number"
                    min={0}
                    max={23}
                    value={String(form.prime_start_hour)}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        pacing_mode: 'custom',
                        prime_start_hour: Number(e.target.value),
                      })
                    }
                    helperText="Hora de início da aceleração (ex: 18h)"
                    error={
                      hasTimeConflict
                        ? 'Início deve ser menor que o término'
                        : undefined
                    }
                  />
                  <Input
                    label="Término"
                    type="number"
                    min={1}
                    max={24}
                    value={String(form.prime_end_hour)}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        pacing_mode: 'custom',
                        prime_end_hour: Number(e.target.value),
                      })
                    }
                    helperText="Hora de término da aceleração (ex: 22h)"
                  />
                </div>
              </div>

              {/* Linha 2: Silêncio Noturno */}
              <div className="border-surface-border/50 border-t pt-3">
                <span className="mb-2 block text-2xs font-medium text-content-muted">
                  Silêncio Noturno (Formato HH:MM)
                </span>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Input
                    label="Início"
                    value={form.quiet_hours_start}
                    disabled={!form.quiet_hours_enabled}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        pacing_mode: 'custom',
                        quiet_hours_start: e.target.value,
                      })
                    }
                    helperText="Pausa nos disparos (ex: 22:30)"
                  />
                  <Input
                    label="Término"
                    value={form.quiet_hours_end}
                    disabled={!form.quiet_hours_enabled}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        pacing_mode: 'custom',
                        quiet_hours_end: e.target.value,
                      })
                    }
                    helperText="Retomada matinal (ex: 07:30)"
                  />
                </div>
              </div>
            </div>

            {/* Bloco B: Intervalos Anti-Ban (Jitter) */}
            <div className="space-y-4 rounded-xl border border-surface-border bg-surface-panel p-4">
              <div className="border-surface-border/60 border-b pb-2">
                <span className="text-xs font-semibold text-content-main">
                  Intervalos entre Disparos (Jitter Anti-Ban)
                </span>
              </div>

              {/* Linha 1: Horário Nobre Delays */}
              <div>
                <span className="mb-2 block text-2xs font-medium text-content-muted">
                  Intervalo no Horário Nobre (Minutos)
                </span>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Input
                    label="Atraso Mínimo"
                    type="number"
                    min={1}
                    max={60}
                    value={String(form.prime_min_delay_minutes)}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        pacing_mode: 'custom',
                        prime_min_delay_minutes: Number(e.target.value),
                      })
                    }
                    helperText="Tempo mínimo entre envios (ex: 3 min)"
                    error={
                      Number(form.prime_min_delay_minutes) >
                      Number(form.prime_max_delay_minutes)
                        ? 'Mínimo maior que máximo'
                        : undefined
                    }
                  />
                  <Input
                    label="Atraso Máximo"
                    type="number"
                    min={1}
                    max={120}
                    value={String(form.prime_max_delay_minutes)}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        pacing_mode: 'custom',
                        prime_max_delay_minutes: Number(e.target.value),
                      })
                    }
                    helperText="Tempo máximo com jitter (ex: 5 min)"
                  />
                </div>
              </div>

              {/* Linha 2: Dia Comercial Delays */}
              <div className="border-surface-border/50 border-t pt-3">
                <span className="mb-2 block text-2xs font-medium text-content-muted">
                  Intervalo no Dia Comercial (Minutos)
                </span>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Input
                    label="Atraso Mínimo"
                    type="number"
                    min={1}
                    max={60}
                    value={String(form.day_min_delay_minutes)}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        pacing_mode: 'custom',
                        day_min_delay_minutes: Number(e.target.value),
                      })
                    }
                    helperText="Ritmo durante o dia (ex: 8 min)"
                    error={
                      Number(form.day_min_delay_minutes) >
                      Number(form.day_max_delay_minutes)
                        ? 'Mínimo maior que máximo'
                        : undefined
                    }
                  />
                  <Input
                    label="Atraso Máximo"
                    type="number"
                    min={1}
                    max={120}
                    value={String(form.day_max_delay_minutes)}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        pacing_mode: 'custom',
                        day_max_delay_minutes: Number(e.target.value),
                      })
                    }
                    helperText="Tempo máximo durante o dia (ex: 15 min)"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Drawer>
  );
};
